/* Copyright (c) 2026 Centrica Soluciones Innovadoras. */
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  API_CONFIG,
  ApiResponse,
  HttpService,
  PageResponse,
  SearchableField,
  buildSearchByTerm,
} from '@nebula-shared/data-access';
import { CuentaBancoPago, CuentasBancoQuery } from '../../domain/bank-account-modal.model';

/** Sobre con el que el backend envuelve la página, cuando la envuelve. */
type ConEnvoltura = { content?: PageResponse<CuentaBancoPago> };

/** Campos por los que busca el término si el proceso no declara los suyos. */
const CAMPOS_BUSQUEDA_POR_DEFECTO: SearchableField[] = [
  { key: 'codigo', type: 'text' },
  { key: 'nombre', type: 'text' },
];

/**
 * Acceso HTTP a Banco / Pagos (TES-010).
 *
 * SAR ASR-PF-ARQ-01 (exclusión intencional): servicio de **proceso** (pagar/rechazar/listar CE),
 * no maestro CRUD. No extiende BaseCrudService; baseUrl solo vía API_CONFIG.
 */
@Injectable({ providedIn: 'root' })
export class BankAccountService {
  private readonly http = inject(HttpService);
  private readonly baseUrl = `${API_CONFIG.treasury.baseUrl}${API_CONFIG.treasury.endpoints.bancoPagos}`;

  /**
   * post /cuentas-banco: cuentas ACTIVAS, paginadas en el servidor.
   *
   * <p>Los filtros de chequera y de saldo son los del proceso pagar y se apagan
   * uno a uno: quien no los necesite —los traslados bancarios ven todas las
   * cuentas activas, con saldo o sin él— omite `soloChequera` y manda
   * `conSaldo: false`.</p>
   */
  getCuentasBanco(query: CuentasBancoQuery = {}): Observable<PageResponse<CuentaBancoPago>> {
    const {
      page = 0,
      size = 10,
      searchTerm,
      sortColumn,
      sortDirection = 'ASC',
      soloChequera,
      conSaldo = true,
      camposBusqueda = CAMPOS_BUSQUEDA_POR_DEFECTO,
    } = query;

    const searchsBy = [
      {
        key: 'estadoCuenta',
        operation: 'MATCH',
        value: 'ACTIVO',
        linkOperator: 'AND',
      },
      ...(soloChequera === undefined
        ? []
        : [
            {
              key: 'operaChequera',
              operation: 'MATCH',
              value: soloChequera ? 'S' : 'N',
              linkOperator: 'AND',
            },
          ]),
      ...(conSaldo
        ? [
            {
              key: 'saldoDisponible',
              operation: 'GREATER_THAN',
              value: 0,
              linkOperator: 'AND',
            },
          ]
        : []),
      ...buildSearchByTerm(searchTerm, camposBusqueda),
    ];

    const ordersBy: { column: string; direction: 'ASC' | 'DESC' }[] = [];
    if (sortColumn) {
      ordersBy.push({ column: sortColumn, direction: sortDirection });
    }

    const body = {
      page: page.toString(),
      size: size.toString(),
      ...(searchsBy.length > 0 && { searchsBy }),
      ...(ordersBy.length > 0 && { ordersBy }),
    };

    return this.http
      .post<PageResponse<CuentaBancoPago>>(`${this.baseUrl}/cuentas-banco`, body)
      .pipe(
        map((response: ApiResponse<PageResponse<CuentaBancoPago>>) => {
          // La página puede venir envuelta (`response.content`) o suelta.
          const envelope = response?.response || (response as unknown as ConEnvoltura);
          const pageData = (envelope?.content || envelope || {}) as Partial<
            PageResponse<CuentaBancoPago>
          >;

          return {
            size: pageData.size || size,
            number: pageData.number || page,
            numberOfElements: pageData.numberOfElements || pageData.content?.length || 0,
            totalElements: pageData.totalElements || 0,
            totalPages: pageData.totalPages || 0,
            content: pageData.content || [],
            empty: !pageData.content || pageData.content.length === 0,
          };
        }),
      );
  }
}
